import React, { useContext, useEffect, useMemo, useState } from "react";
import { FixedIcon } from "../../../atoms/icons/fixed";
import { ArmIcon } from "../../../atoms/icons/arm";
import { addTrailingZeroes } from "../../../util/addTrailingZeroes";
import { dollarFormatter } from "../../../util/dollarFormatter";
import Chart from "chart.js/auto";
import { AddButton } from "./AddButton";
import { SelectButton } from "./SelectButton";
import { getOneTwelfthValue } from "../../../util/getOneTwelfthValue";
import { CloseIcon } from "../../../atoms/icons/close";
import { RemoveModal } from "./RemoveModal";
import IconButton from "@mui/material/IconButton";
import { EditIcon } from "../../../atoms/icons/edit";
import { EditWrapper } from "../../modals/modal-utils";
import { PaperItem, QuoteCardAccordion } from "./CustomAccordion";
import { useClosingCalculations } from "../../../util/hooks/useClosingCalculations";
import {
  InsuranceDesktopContainer,
  InterestDesktopContainer,
  PieChartContainer,
  PmiDesktopContainer,
  PrincipalDesktopContainer,
  TaxesDesktopContainer,
} from "./ui-utils";
import { LookingToContext } from "../../../context/LookingToContext";
import { isRefinance } from "../../../util/compare_utils";

export const QuoteCard = ({
  quote,
  openArray,
  setShowAddModal,
  enteredData,
  setComparedId,
  isComparedIds,
  onEditClick,
  onSelect,
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const lookingTo = useContext(LookingToContext);

  const {
    insurance,
    taxes,
    downPayment = 0,
    insurancePaymentMonth,
    plannedClosingDate,
    earnestMoney,
    credits,
    hasWaivedEscrow,
    state,
  } = enteredData;

  const {
    sumOfCredits,
    sumOfEscrows,
    escrows,
    sumOfPrePaids,
    prePaids,
    sumOfPrimaryItems,
    primaryItems,
    sumOfGovFees,
    governmentFees,
    sumOfCannotShopFor,
    servicesBorrowerCannotShopFor,
    sumOfLenderFees,
    lenderFees,
    titleFees,
    term,
    id,
    lenderCredit,
    principal,
    interest,
    pmi,
    amortizationType,
    loanTerm,
    rate,
    apr,
    monthlyPayment,
    closingCosts,
    totalCashToClose,
  } = useClosingCalculations({
    details: quote,
    downPayment,
    insurancePaymentMonth,
    plannedClosingDate,
    hasWaivedEscrow,
    insurance,
    taxes,
    state,
    earnestMoney,
    credits,
    lookingTo,
  });

  const pieChartData = useMemo(
    () => ({
      datasets: [
        {
          data: [
            principal,
            interest,
            pmi,
            getOneTwelfthValue(insurance),
            getOneTwelfthValue(taxes),
          ],
          backgroundColor: [
            "#205590",
            "#e7174b",
            "#600e2a",
            "#3a9ed7",
            "#666666",
          ],
          borderWidth: 0,
        },
      ],
    }),
    [interest, pmi, insurance, taxes, principal]
  );

  const chartCanvasRef = React.useRef();

  useEffect(() => {
    const chart = new Chart(chartCanvasRef.current, {
      type: "doughnut",
      data: pieChartData,
      options: {
        cutout: 0,
        radius: 50,
        plugins: { legend: { display: false } },
      },
    });

    return () => {
      chart.destroy();
    };
  }, [pieChartData]);

  return (
    <>
      <RemoveModal
        open={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
        amortizationType={amortizationType}
        loanTerm={loanTerm}
        term={term}
        rate={rate}
        apr={apr}
        monthlyPayment={monthlyPayment}
        setComparedId={setComparedId}
        id={id}
        isComparedIds={isComparedIds}
      />
      <div className="quote-card desktop-quote-card">
        {isComparedIds.length > 1 && (
          <div className="close-quote" onClick={() => setShowRemoveModal(true)}>
            <CloseIcon stroke="grey" width="30px" height="30px" />
          </div>
        )}
        {amortizationType === "Fixed" ? (
          <div className="quote-card-icon-wrapper">
            <FixedIcon fill="#e7174b" width="40px" height="40px" />
            <span className="quote-title">Fixed</span>
            <span>Conventional</span>
          </div>
        ) : (
          <div className="quote-card-icon-wrapper">
            <ArmIcon fill="#e7174b" width="40px" height="40px" />
            <span className="quote-title">ARM</span>
            <span>Conventional</span>
          </div>
        )}
        <div className="large bold">
          {amortizationType === "Fixed" ? `${loanTerm} year` : `${term}/6`}
        </div>
        <div className="large bold">{addTrailingZeroes(rate)}%</div>
        <div className="large bold">{addTrailingZeroes(apr)}%</div>
        <div className="horizontal-line" />
        <div className="large bold">{dollarFormatter(monthlyPayment, 2)}</div>
        <PieChartContainer>
          <canvas ref={chartCanvasRef} />
        </PieChartContainer>
        <PrincipalDesktopContainer>
          {dollarFormatter(principal, 2)}
        </PrincipalDesktopContainer>
        <InterestDesktopContainer>
          {dollarFormatter(interest, 2)}
        </InterestDesktopContainer>
        <PmiDesktopContainer>{dollarFormatter(pmi, 2)}</PmiDesktopContainer>

        {!insurance ? (
          <AddButton handleClick={() => setShowAddModal(true)} />
        ) : (
          <EditWrapper>
            <InsuranceDesktopContainer>
              {dollarFormatter(getOneTwelfthValue(insurance))}
            </InsuranceDesktopContainer>
            <IconButton aria-label="Edit loan selections" onClick={onEditClick}>
              <EditIcon fill="#bbb" width="16px" height="16px" />
            </IconButton>
          </EditWrapper>
        )}
        {taxes ? (
          <EditWrapper>
            <TaxesDesktopContainer>
              {dollarFormatter(getOneTwelfthValue(taxes))}
            </TaxesDesktopContainer>
            <IconButton aria-label="Edit loan selections" onClick={onEditClick}>
              <EditIcon fill="#bbb" width="16px" height="16px" />
            </IconButton>
          </EditWrapper>
        ) : (
          <AddButton handleClick={() => setShowAddModal(true)} />
        )}
        <div className="horizontal-line" />
        <div className="totals-wrapper">
          {dollarFormatter(Math.round(closingCosts))}
        </div>
        <QuoteCardAccordion openArray={openArray} name="ClosingCosts">
          <div className="quote-card-accordion-content">
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(sumOfLenderFees))}
            </div>
            <QuoteCardAccordion openArray={openArray} name="LenderFees">
              {lenderFees.map((item) => (
                <PaperItem key={item.title} dark padding="5px 0" centered>
                  {dollarFormatter(Math.round(item.amount))}
                </PaperItem>
              ))}
            </QuoteCardAccordion>
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(sumOfCannotShopFor))}
            </div>
            <QuoteCardAccordion openArray={openArray} name="CannotShopFor">
              {servicesBorrowerCannotShopFor.map((item) => (
                <PaperItem key={item.title} dark padding="5px 0" centered>
                  {dollarFormatter(Math.round(item.amount))}
                </PaperItem>
              ))}
            </QuoteCardAccordion>
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(titleFees))}
            </div>
            <QuoteCardAccordion openArray={openArray} name="CanShopFor">
              <PaperItem dark padding="5px 0" centered>
                {dollarFormatter(Math.round(titleFees))}
              </PaperItem>
            </QuoteCardAccordion>
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(sumOfGovFees))}
            </div>
            <QuoteCardAccordion openArray={openArray} name="GovernmentFees">
              {governmentFees.map((item) => (
                <PaperItem key={item.title} dark padding="5px 0" centered>
                  {dollarFormatter(Math.round(item.amount))}
                </PaperItem>
              ))}
            </QuoteCardAccordion>
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(lenderCredit))}
            </div>
          </div>
        </QuoteCardAccordion>
        <div className="horizontal-line" />
        <div className="totals-wrapper">
          {downPayment ||
          credits ||
          earnestMoney ||
          (rate && plannedClosingDate) ||
          (insurance && insurancePaymentMonth) ? (
            <div>{dollarFormatter(Math.round(totalCashToClose))}</div>
          ) : (
            <AddButton handleClick={() => setShowAddModal(true)} />
          )}
        </div>
        <QuoteCardAccordion openArray={openArray} name="CashToClose">
          <div className="quote-card-accordion-content">
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(sumOfPrimaryItems))}
            </div>
            <QuoteCardAccordion openArray={openArray} name="PrimaryItems">
              {primaryItems.map((item) =>
                item.title === "Down Payment" &&
                lookingTo === "Refinance" ? null : (
                  <PaperItem key={item.title} dark padding="5px 0" centered>
                    {dollarFormatter(Math.round(item.amount))}
                  </PaperItem>
                )
              )}
            </QuoteCardAccordion>
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(sumOfPrePaids))}
            </div>
            <QuoteCardAccordion openArray={openArray} name="Prepaids">
              {prePaids.map((item) => (
                <PaperItem key={item.title} dark padding="5px 0" centered>
                  {dollarFormatter(Math.round(item.amount))}
                </PaperItem>
              ))}
            </QuoteCardAccordion>
            <div className="quote-card-accordion-items bold">
              {dollarFormatter(Math.round(sumOfEscrows))}
            </div>
            <QuoteCardAccordion
              openArray={openArray}
              name="Escrows"
              isBottom={isRefinance()}
            >
              {escrows.map((item) => (
                <PaperItem key={item.title} dark padding="5px 0" centered>
                  {dollarFormatter(Math.round(item.amount))}
                </PaperItem>
              ))}
            </QuoteCardAccordion>
            {!isRefinance() && (
              <>
                <div className="quote-card-accordion-items bold">
                  {sumOfCredits && sumOfCredits > 0 ? "-" : ""}
                  {dollarFormatter(Math.round(sumOfCredits))}
                </div>
                <QuoteCardAccordion
                  openArray={openArray}
                  name="Credits"
                  isBottom
                >
                  <PaperItem key="Earnest Money" dark padding="5px 0" centered>
                    {dollarFormatter(Math.round(earnestMoney))}
                  </PaperItem>
                  <PaperItem
                    key="Seller/Realtor Credit"
                    dark
                    padding="5px 0"
                    centered
                  >
                    {dollarFormatter(Math.round(credits))}
                  </PaperItem>
                </QuoteCardAccordion>
              </>
            )}
          </div>
        </QuoteCardAccordion>
        <div className="horizontal-line" />
        <SelectButton label="Select" handleClick={onSelect} />
      </div>
    </>
  );
};
